import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, PageBody, fmtMoney } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Handshake, ArrowsClockwise, ArrowUUpLeft, ClockCounterClockwise } from "@phosphor-icons/react";

export default function IntercompanyPage() {
  const { companies, formatApiError } = useAuth();
  const [rows, setRows] = useState([]);
  const [reversedRows, setReversedRows] = useState([]);
  const [showReversed, setShowReversed] = useState(false);
  const [open, setOpen] = useState(false);
  const [repayOpen, setRepayOpen] = useState(false);
  const [activeLoan, setActiveLoan] = useState(null);
  const [repayAmt, setRepayAmt] = useState("");
  const [repayDate, setRepayDate] = useState(new Date().toISOString().slice(0, 10));
  const [form, setForm] = useState({
    lender_company_id: "",
    borrower_company_id: "",
    amount: "",
    date: new Date().toISOString().slice(0, 10),
    memo: "",
    lender_ic_gl_code: "",
    lender_ic_gl_name: "",
    borrower_ic_gl_code: "",
    borrower_ic_gl_name: "",
  });

  const load = async () => {
    const [{ data: active }, { data: reversed }] = await Promise.all([
      api.get("/intercompany/loans"),
      api.get("/intercompany/reversed"),
    ]);
    setRows(active);
    setReversedRows(reversed);
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.lender_company_id || !form.borrower_company_id) {
      toast.error("Select lender and borrower");
      return;
    }
    if (form.lender_company_id === form.borrower_company_id) {
      toast.error("Lender and borrower must differ");
      return;
    }
    try {
      await api.post("/intercompany/loans", {
        ...form,
        amount: parseFloat(form.amount),
      });
      toast.success("Intercompany loan posted to both companies");
      setOpen(false);
      setForm({
        lender_company_id: "",
        borrower_company_id: "",
        amount: "",
        date: new Date().toISOString().slice(0, 10),
        memo: "",
        lender_ic_gl_code: "",
        lender_ic_gl_name: "",
        borrower_ic_gl_code: "",
        borrower_ic_gl_name: "",
      });
      load();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  const reverseLoan = async (loan) => {
    const isBankIC = loan.source === "bank_holding";
    const msg = isBankIC
      ? `Reverse this bank-holding intercompany posting?\n\nThis will:\n  • Post reversing journals on both companies\n  • Delete the loan record\n  • Restore the bank item back to Holding so you can re-post it correctly`
      : "This loan wasn't created from a bank holding item. Only bank-holding-originated IC entries can be reversed here. Use Repay instead.";
    if (!isBankIC) { toast.error(msg); return; }
    if (!window.confirm(msg)) return;
    try {
      await api.post(`/intercompany/loans/${loan.id}/reverse`);
      toast.success("Reversed — journals posted on both companies. Bank item restored to holding.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const openRepay = (loan) => {
    setActiveLoan(loan);
    setRepayAmt("");
    setRepayDate(new Date().toISOString().slice(0, 10));
    setRepayOpen(true);
  };

  const submitRepay = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/intercompany/loans/${activeLoan.id}/repay`, {
        loan_id: activeLoan.id,
        amount: parseFloat(repayAmt),
        date: repayDate,
      });
      toast.success("Repayment posted");
      setRepayOpen(false);
      load();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  const totalOutstanding = rows.reduce((s, r) => s + (r.outstanding || 0), 0);

  return (
    <>
      <PageHeader
        eyebrow="Multi-Entity"
        title="Intercompany Loans"
        description="Move cash between companies you control. LedgerLoom posts both sides atomically and auto-eliminates the balances during group consolidation."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="new-ic-loan"
                className="rounded-sm h-9 font-mono uppercase tracking-wider text-xs"
              >
                <Plus size={14} className="mr-2" /> New Loan
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-serif-display text-2xl">New Intercompany Loan</DialogTitle>
              </DialogHeader>
              <form onSubmit={create} className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest">Lender (paying company)</Label>
                  <Select
                    value={form.lender_company_id}
                    onValueChange={(v) => setForm({ ...form, lender_company_id: v })}
                  >
                    <SelectTrigger data-testid="ic-lender" className="rounded-sm font-mono">
                      <SelectValue placeholder="Select lender" />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map((c) => (
                        <SelectItem key={c.id} value={c.id} className="font-mono">
                          {c.name} <span className="text-xs text-muted-foreground ml-2">({c.currency})</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Borrower (receiving company)</Label>
                  <Select
                    value={form.borrower_company_id}
                    onValueChange={(v) => setForm({ ...form, borrower_company_id: v })}
                  >
                    <SelectTrigger data-testid="ic-borrower" className="rounded-sm font-mono">
                      <SelectValue placeholder="Select borrower" />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map((c) => (
                        <SelectItem key={c.id} value={c.id} className="font-mono">
                          {c.name} <span className="text-xs text-muted-foreground ml-2">({c.currency})</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Amount</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.amount}
                      onChange={(e) => setForm({ ...form, amount: e.target.value })}
                      required
                      data-testid="ic-amount"
                      className="rounded-sm font-mono text-right"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Date</Label>
                    <Input
                      type="date"
                      value={form.date}
                      onChange={(e) => setForm({ ...form, date: e.target.value })}
                      required
                      className="rounded-sm font-mono"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Memo</Label>
                  <Textarea
                    value={form.memo}
                    onChange={(e) => setForm({ ...form, memo: e.target.value })}
                    rows={2}
                    className="rounded-sm font-mono"
                    placeholder="e.g. Working capital support"
                  />
                </div>
                <div className="qb-card p-3 space-y-3">
                  <div className="overline">Intercompany GL Accounts (optional overrides)</div>
                  <div className="text-xs text-muted-foreground">
                    Leave blank to auto-generate. Enter a code (and optional name) to reuse an existing GL or create a new one with that code.
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Lender IC GL Code</Label>
                      <Input
                        value={form.lender_ic_gl_code}
                        onChange={(e) => setForm({ ...form, lender_ic_gl_code: e.target.value })}
                        placeholder="e.g. 1301"
                        data-testid="ic-lender-gl-code"
                        className="rounded-sm font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Lender IC GL Name</Label>
                      <Input
                        value={form.lender_ic_gl_name}
                        onChange={(e) => setForm({ ...form, lender_ic_gl_name: e.target.value })}
                        placeholder="Optional"
                        data-testid="ic-lender-gl-name"
                        className="rounded-sm font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Borrower IC GL Code</Label>
                      <Input
                        value={form.borrower_ic_gl_code}
                        onChange={(e) => setForm({ ...form, borrower_ic_gl_code: e.target.value })}
                        placeholder="e.g. 2301"
                        data-testid="ic-borrower-gl-code"
                        className="rounded-sm font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Borrower IC GL Name</Label>
                      <Input
                        value={form.borrower_ic_gl_name}
                        onChange={(e) => setForm({ ...form, borrower_ic_gl_name: e.target.value })}
                        placeholder="Optional"
                        data-testid="ic-borrower-gl-name"
                        className="rounded-sm font-mono"
                      />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="submit"
                    data-testid="ic-submit"
                    className="rounded-sm font-mono uppercase tracking-wider text-xs"
                  >
                    Post Both Sides
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      <PageBody>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
          <div className="bg-card p-6">
            <div className="overline">Active Loans</div>
            <div className="font-serif-display text-3xl num mt-2">
              {rows.filter((r) => r.outstanding > 0).length}
            </div>
          </div>
          <div className="bg-card p-6 md:col-span-2">
            <div className="overline">Total Outstanding (mixed currency, not converted)</div>
            <div className="font-serif-display text-3xl num mt-2">
              {totalOutstanding.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
        </div>

        <div className="border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/40">
              <tr className="text-left">
                <Th>Date</Th>
                <Th>Lender</Th>
                <Th>Borrower</Th>
                <Th className="text-right">Principal</Th>
                <Th className="text-right">Outstanding</Th>
                <Th>Memo</Th>
                <Th className="text-right pr-4">Action</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`ic-loan-row-${r.id}`}
                  className="border-b border-border last:border-b-0 hover:bg-muted/40 transition-colors duration-200"
                >
                  <Td className="num">{r.date}</Td>
                  <Td className="font-mono flex items-center gap-2">
                    <Handshake size={14} className="text-primary" />
                    {r.lender_name}
                  </Td>
                  <Td className="font-mono">{r.borrower_name}</Td>
                  <Td className="text-right num">{r.principal.toLocaleString()}</Td>
                  <Td className={`text-right num font-medium ${r.outstanding > 0 ? "" : "text-primary"}`}>
                    {r.outstanding.toLocaleString()}
                    {r.outstanding === 0 && <span className="ml-2 tag-badge text-primary">SETTLED</span>}
                  </Td>
                  <Td className="text-xs text-muted-foreground">{r.memo}</Td>
                  <Td className="text-right pr-4">
                    {r.source === "bank_holding" && (
                      <span className="mr-2 tag-badge text-primary" title="Created from Bank Holding">BANK</span>
                    )}
                    {r.outstanding > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openRepay(r)}
                        data-testid={`ic-repay-${r.id}`}
                        className="rounded-sm h-8 font-mono text-xs uppercase tracking-wider"
                      >
                        <ArrowsClockwise size={12} className="mr-1" /> Repay
                      </Button>
                    )}
                    {r.source === "bank_holding" && r.outstanding === r.principal && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => reverseLoan(r)}
                        data-testid={`ic-reverse-${r.id}`}
                        className="rounded-sm h-8 font-mono text-xs uppercase tracking-wider text-destructive ml-2"
                      >
                        <ArrowUUpLeft size={12} className="mr-1" /> Reverse
                      </Button>
                    )}
                  </Td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-muted-foreground text-sm">
                    <Handshake size={20} className="inline mr-2 opacity-40" />
                    No intercompany loans yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Reversed audit log — greyed-out historical view */}
        <div className="mt-8" data-testid="ic-reversed-section">
          <button
            type="button"
            onClick={() => setShowReversed((v) => !v)}
            className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors mb-2"
            data-testid="ic-reversed-toggle"
          >
            <ClockCounterClockwise size={14} />
            {showReversed ? "Hide" : "Show"} reversed postings ({reversedRows.length})
          </button>
          {showReversed && (
            <div
              className="border border-dashed border-border overflow-hidden opacity-70 grayscale"
              data-testid="ic-reversed-table"
            >
              <div className="px-4 py-2 text-[10px] uppercase tracking-widest bg-muted/40 text-muted-foreground border-b border-dashed border-border">
                Read-only audit trail · Reversed entries cannot be reactivated
              </div>
              <table className="w-full text-sm">
                <thead className="bg-muted/20 text-muted-foreground">
                  <tr>
                    <Th>Date</Th>
                    <Th>Lender</Th>
                    <Th>Borrower</Th>
                    <Th className="text-right">Principal</Th>
                    <Th>Reversed on</Th>
                    <Th>Reversed by</Th>
                    <Th>Memo</Th>
                  </tr>
                </thead>
                <tbody>
                  {reversedRows.map((r) => (
                    <tr
                      key={r.id}
                      className="border-t border-dashed border-border line-through"
                      data-testid={`ic-reversed-row-${r.id}`}
                    >
                      <Td className="text-muted-foreground">{r.date}</Td>
                      <Td className="text-muted-foreground">{r.lender_name}</Td>
                      <Td className="text-muted-foreground">{r.borrower_name}</Td>
                      <Td className="text-right num text-muted-foreground">
                        {fmtMoney(r.principal, r.currency)}
                      </Td>
                      <Td className="text-muted-foreground">
                        {r.reversed_at ? String(r.reversed_at).slice(0, 10) : "—"}
                      </Td>
                      <Td className="text-muted-foreground">{r.reversed_by_name || "—"}</Td>
                      <Td className="text-muted-foreground max-w-[260px] truncate" title={r.memo}>
                        {r.memo || "—"}
                      </Td>
                    </tr>
                  ))}
                  {reversedRows.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center py-6 text-muted-foreground text-xs">
                        No reversed postings on record.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <Dialog open={repayOpen} onOpenChange={setRepayOpen}>
          <DialogContent className="rounded-sm">
            <DialogHeader>
              <DialogTitle className="font-serif-display text-2xl">Loan Repayment</DialogTitle>
            </DialogHeader>
            {activeLoan && (
              <form onSubmit={submitRepay} className="space-y-3">
                <div className="border border-border p-4 text-sm space-y-1">
                  <div><span className="overline mr-2">Lender</span> {activeLoan.lender_name}</div>
                  <div><span className="overline mr-2">Borrower</span> {activeLoan.borrower_name}</div>
                  <div><span className="overline mr-2">Outstanding</span> <span className="num">{activeLoan.outstanding.toLocaleString()}</span></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Amount</Label>
                    <Input
                      type="number"
                      step="0.01"
                      max={activeLoan.outstanding}
                      value={repayAmt}
                      onChange={(e) => setRepayAmt(e.target.value)}
                      required
                      data-testid="ic-repay-amount"
                      className="rounded-sm font-mono text-right"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Date</Label>
                    <Input
                      type="date"
                      value={repayDate}
                      onChange={(e) => setRepayDate(e.target.value)}
                      required
                      className="rounded-sm font-mono"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="submit"
                    data-testid="ic-repay-submit"
                    className="rounded-sm font-mono uppercase tracking-wider text-xs"
                  >
                    Post Repayment
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </PageBody>
    </>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 overline ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 ${className}`}>{children}</td>
);
